import React, { createContext, ReactElement, useCallback, useState } from 'react';
import { localStorageItems } from 'setup/localStorage';
import { LoginCreds } from 'types';
import { axiosInstance } from 'setup/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { pagePaths } from 'routes/paths';

interface IAuthContextProps {
  children: ReactElement;
}

interface IAuthContext {
  isAuthed: boolean;
  authUser: (creds: LoginCreds) => Promise<any>;
  authGoogleUser: (credential: string) => Promise<any>;
  logout: () => void;
}

export const AuthContext = createContext({} as IAuthContext);

const AuthProvider: React.FC<IAuthContextProps> = ({ children }) => {
  const [isAuthed, setIsAuthed] = useState(
    !!localStorage.getItem(localStorageItems.accessToken),
  );

  const navigate = useNavigate();

  const authUser: IAuthContext['authUser'] = useCallback(async (creds) => {
    const { data } = await axiosInstance.post<{ token: string }>(
      `${import.meta.env.VITE_BASE_API_URL.replace('/api/', '')}/auth/login`,
      creds,
    );

    if (data.token) {
      localStorage.setItem(localStorageItems.accessToken, data.token);
      setIsAuthed(true);
      navigate(pagePaths.wordset);
    }
  }, [navigate]);

  const authGoogleUser: IAuthContext['authGoogleUser'] = useCallback(async (credential) => {
    const { data } = await axiosInstance.post<{ token: string }>(
      `${import.meta.env.VITE_BASE_API_URL.replace('/api/', '')}/auth/google`,
      { credential },
    );

    if (data.token) {
      localStorage.setItem(localStorageItems.accessToken, data.token);
      setIsAuthed(true);
      navigate(pagePaths.wordset);
    }
  }, [navigate]);

  const logout = () => {
    setIsAuthed(false);
    localStorage.removeItem(localStorageItems.accessToken);
    navigate(pagePaths.index);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthed,
        authUser,
        authGoogleUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
